import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  Paper,
  Button,
  TextField,
  Grid,
  Snackbar,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Autocomplete,
  Typography,
  Chip,
  Stack,
  Collapse,
  IconButton,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  LinearProgress,
} from "@mui/material";

import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddCardIcon from "@mui/icons-material/AddCard";
import SummarizeIcon from "@mui/icons-material/Summarize";
import SchoolIcon from "@mui/icons-material/School";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import GroupsIcon from "@mui/icons-material/Groups";
import BadgeIcon from "@mui/icons-material/Badge";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PaymentsIcon from "@mui/icons-material/Payments";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { API_URL } from "./config";

/* ------------------------- Theme helpers ------------------------- */
const THEME_ORANGE = "#F07828";
const THEME_ORANGE_2 = "#ed7f35ff";
const BG = "#F2F2F2";
const BORDER = "rgba(25,118,210,0.20)";
const BORDER_HOVER = "rgba(25,118,210,0.45)";
const BORDER_FOCUS = "rgba(240,120,40,0.80)";
const ROW_ALT = "rgba(25,118,210,0.06)";
const ROW_HOVER = "rgba(25,118,210,0.10)";

const pageWrapSx = { p: 2, backgroundColor: BG };

const rootPaperSx = {
  mx: "auto",
  borderRadius: 2,
  overflow: "hidden",
  border: "1px solid rgba(25,118,210,0.15)",
};

const headerSx = {
  px: 2,
  py: 1.5,
  background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  flexWrap: "wrap",
};

const bodySx = { p: 2, backgroundColor: BG };

const surfaceSx = {
  borderRadius: 2,
  backgroundColor: "white",
  border: `1px solid ${BORDER}`,
};

const sectionSx = {
  ...surfaceSx,
  p: 2,
  position: "relative",
  overflow: "hidden",
  "&:before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    opacity: 0.95,
  },
};

const titleRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 1,
};

const titleIconSx = {
  color: THEME_ORANGE,
  fontSize: 22,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: "#fff",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
};

const primaryBtnSx = {
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 900,
  backgroundColor: THEME_ORANGE,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: THEME_ORANGE_2,
    boxShadow: "none",
  },
};

const outlinedBtnSx = {
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 900,
  borderColor: "rgba(25,118,210,0.35)",
  color: "rgba(25,118,210,0.95)",
  backgroundColor: "#fff",
  "&:hover": {
    borderColor: "rgba(25,118,210,0.55)",
    backgroundColor: "rgba(25,118,210,0.04)",
  },
};

const iconBtnSx = {
  borderRadius: 2,
  color: "rgba(25,118,210,0.95)",
  "&:hover": {
    backgroundColor: "rgba(240,120,40,0.10)",
    color: THEME_ORANGE,
  },
};

const tableCardSx = {
  ...surfaceSx,
  mt: 2,
  p: 0,
  overflow: "hidden",
};

const scrollClipSx = {
  width: "100%",
  overflowX: "auto",
  overflowY: "hidden",
  borderRadius: 2,
};

const tableSx = {
  minWidth: "max-content",
  "& th, & td": {
    whiteSpace: "nowrap",
  },
};

const theadRowSx = {
  backgroundColor: "rgba(25,118,210,0.95)",
  "& th": {
    color: "#fff",
    fontWeight: 900,
    borderBottom: "1px solid rgba(255,255,255,0.18)",
    whiteSpace: "nowrap",
    py: 1,
  },
};

const zebraRowSx = (idx, clickable = false) => ({
  backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
  cursor: clickable ? "pointer" : "default",
  userSelect: clickable ? "none" : "auto",
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: clickable ? "rgba(240,120,40,0.06)" : ROW_HOVER,
  },
  "& td": {
    borderBottom: "1px solid rgba(25,118,210,0.10)",
    py: 0.8,
  },
});

const chipSx = {
  backgroundColor: "rgba(25,118,210,0.06)",
  border: "1px solid rgba(25,118,210,0.14)",
  "& .MuiChip-deleteIcon": { color: "rgba(25,118,210,0.75)" },
  "& .MuiChip-deleteIcon:hover": { color: THEME_ORANGE },
};

const headCell = (icon, label, extraSx = {}) => (
  <TableCell sx={extraSx}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {icon}
      {label}
    </Box>
  </TableCell>
);

/* ------------------------- Drilldown component ------------------------- */
function FeeSummaryDrilldown({ campusCode, feeSummary }) {
  const [openTop, setOpenTop] = useState({});
  const [openBranch, setOpenBranch] = useState({});
  const [openSem, setOpenSem] = useState({});

  const [branches, setBranches] = useState({});
  const [semesters, setSemesters] = useState({});
  const [students, setStudents] = useState({});

  const [loadingKey, setLoadingKey] = useState(null);
  const [err, setErr] = useState("");
  const token = localStorage.getItem("auth");

  const convertToISTDate = (utcTime) =>
    utcTime
      ? new Date(utcTime).toLocaleDateString("en-CA", {
          timeZone: "Asia/Kolkata",
        })
      : "";

  const makeTopKey = (row) =>
    `${row.FeeStructureID}|${row.DegreeCode}|${convertToISTDate(row.FeeDate)}`;

  const loadBranches = async (row) => {
    const topKey = makeTopKey(row);
    if (branches[topKey]) return;

    try {
      setLoadingKey(topKey);
      const { data } = await axios.get(`${API_URL}/api/fee-summary/branches`, {
        params: {
          FeeStructureID: row.FeeStructureID,
          DegreeCode: row.DegreeCode,
          FeeDate: convertToISTDate(row.FeeDate),
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches((m) => ({ ...m, [topKey]: data }));
    } catch {
      setErr("Failed to load branches");
    } finally {
      setLoadingKey(null);
    }
  };

  const loadSemesters = async (row, branchCode) => {
    const topKey = makeTopKey(row);
    const brKey = `${topKey}|${branchCode}`;

    if (semesters[brKey]) return;

    try {
      setLoadingKey(brKey);
      const { data } = await axios.get(`${API_URL}/api/fee-summary/semesters`, {
        params: {
          FeeStructureID: row.FeeStructureID,
          DegreeCode: row.DegreeCode,
          BranchCode: branchCode,
          FeeDate: convertToISTDate(row.FeeDate),
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSemesters((m) => ({ ...m, [brKey]: data }));
    } catch {
      setErr("Failed to load semesters");
    } finally {
      setLoadingKey(null);
    }
  };

  const loadStudents = async (row, branchCode, semester) => {
    const topKey = makeTopKey(row);
    const semKey = `${topKey}|${branchCode}|${semester}`;

    if (students[semKey]) return;

    try {
      setLoadingKey(semKey);
      const { data } = await axios.get(`${API_URL}/api/fee-summary/students`, {
        params: {
          FeeStructureID: row.FeeStructureID,
          DegreeCode: row.DegreeCode,
          BranchCode: branchCode,
          Semester: semester,
          FeeDate: convertToISTDate(row.FeeDate),
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents((m) => ({ ...m, [semKey]: data }));
    } catch {
      setErr("Failed to load students");
    } finally {
      setLoadingKey(null);
    }
  };

  const toggleTop = async (row) => {
    const topKey = makeTopKey(row);
    if (!openTop[topKey]) await loadBranches(row);
    setOpenTop((m) => ({ ...m, [topKey]: !m[topKey] }));
  };

  const toggleBranch = async (row, branchCode) => {
    const topKey = makeTopKey(row);
    const brKey = `${topKey}|${branchCode}`;
    if (!openBranch[brKey]) await loadSemesters(row, branchCode);
    setOpenBranch((m) => ({ ...m, [brKey]: !m[brKey] }));
  };

  const toggleSem = async (row, branchCode, semester) => {
    const topKey = makeTopKey(row);
    const semKey = `${topKey}|${branchCode}|${semester}`;
    if (!openSem[semKey]) await loadStudents(row, branchCode, semester);
    setOpenSem((m) => ({ ...m, [semKey]: !m[semKey] }));
  };

  return (
    <>
      {err && (
        <Alert
          severity="error"
          onClose={() => setErr("")}
          sx={{
            mt: 2,
            borderRadius: 2,
            border: "1px solid rgba(244,67,54,0.25)",
            backgroundColor: "rgba(244,67,54,0.04)",
          }}
        >
          {err}
        </Alert>
      )}

      <Paper elevation={0} sx={tableCardSx}>
        <Box sx={scrollClipSx}>
          <TableContainer component="div">
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow sx={theadRowSx}>
                  <TableCell />
                  {headCell(<SchoolIcon fontSize="small" />, "Degree")}
                  {headCell(<PaymentsIcon fontSize="small" />, "Fee Type")}
                  {headCell(<CalendarMonthIcon fontSize="small" />, "Fee Date")}
                  {headCell(<GroupsIcon fontSize="small" />, "Number of Entries")}
                </TableRow>
              </TableHead>

              <TableBody>
                {feeSummary.map((row, index) => {
                  const topKey = makeTopKey(row);
                  const isOpen = !!openTop[topKey];

                  return (
                    <React.Fragment key={topKey}>
                      <TableRow
                        sx={zebraRowSx(index, true)}
                        onClick={() => toggleTop(row)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleTop(row);
                          }
                        }}
                        aria-expanded={isOpen}
                      >
                        <TableCell>
                          <Tooltip title={isOpen ? "Collapse" : "Expand"}>
                            <IconButton
                              sx={iconBtnSx}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTop(row);
                              }}
                            >
                              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>

                        <TableCell>{row.DegreeCode}</TableCell>
                        <TableCell>{row.FeeType}</TableCell>
                        <TableCell>{convertToISTDate(row.FeeDate)}</TableCell>
                        <TableCell>{row.NumberOfEntries}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ m: 1, ...surfaceSx, overflow: "hidden" }}>
                              <Box sx={{ p: 1.5 }}>
                                <Box sx={titleRowSx}>
                                  <AccountTreeIcon sx={titleIconSx} />
                                  <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                                    Branch-wise Breakdown
                                  </Typography>
                                </Box>

                                {loadingKey === topKey ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <Paper elevation={0} sx={{ ...tableCardSx, mt: 0 }}>
                                    <Box sx={scrollClipSx}>
                                      <Table size="small" sx={tableSx}>
                                        <TableHead>
                                          <TableRow sx={theadRowSx}>
                                            <TableCell />
                                            {headCell(<AccountTreeIcon fontSize="small" />, "Branch")}
                                            {headCell(<GroupsIcon fontSize="small" />, "Count")}
                                          </TableRow>
                                        </TableHead>

                                        <TableBody>
                                          {(branches[topKey] || []).map((b, bi) => {
                                            const brKey = `${topKey}|${b.BranchCode}`;
                                            const brOpen = !!openBranch[brKey];

                                            return (
                                              <React.Fragment key={brKey}>
                                                <TableRow
                                                  sx={zebraRowSx(bi, true)}
                                                  onClick={() => toggleBranch(row, b.BranchCode)}
                                                  role="button"
                                                  tabIndex={0}
                                                  onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                      e.preventDefault();
                                                      toggleBranch(row, b.BranchCode);
                                                    }
                                                  }}
                                                  aria-expanded={brOpen}
                                                >
                                                  <TableCell>
                                                    <Tooltip title={brOpen ? "Collapse" : "Expand"}>
                                                      <IconButton
                                                        sx={iconBtnSx}
                                                        size="small"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          toggleBranch(row, b.BranchCode);
                                                        }}
                                                      >
                                                        {brOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                      </IconButton>
                                                    </Tooltip>
                                                  </TableCell>

                                                  <TableCell>{b.BranchCode}</TableCell>
                                                  <TableCell>{b.Count}</TableCell>
                                                </TableRow>

                                                <TableRow>
                                                  <TableCell colSpan={3} sx={{ p: 0, border: 0 }}>
                                                    <Collapse in={brOpen} timeout="auto" unmountOnExit>
                                                      <Box sx={{ ml: 2, my: 1, ...surfaceSx, overflow: "hidden" }}>
                                                        <Box sx={{ p: 1.5 }}>
                                                          <Box sx={titleRowSx}>
                                                            <SchoolIcon sx={titleIconSx} />
                                                            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                                                              Semester-wise Breakdown
                                                            </Typography>
                                                          </Box>

                                                          {loadingKey === brKey ? (
                                                            <CircularProgress size={16} />
                                                          ) : (
                                                            <Paper elevation={0} sx={{ ...tableCardSx, mt: 0 }}>
                                                              <Box sx={scrollClipSx}>
                                                                <Table size="small" sx={tableSx}>
                                                                  <TableHead>
                                                                    <TableRow sx={theadRowSx}>
                                                                      <TableCell />
                                                                      {headCell(
                                                                        <SchoolIcon fontSize="small" />,
                                                                        "Semester"
                                                                      )}
                                                                      {headCell(
                                                                        <GroupsIcon fontSize="small" />,
                                                                        "Count"
                                                                      )}
                                                                    </TableRow>
                                                                  </TableHead>

                                                                  <TableBody>
                                                                    {(semesters[brKey] || []).map((s, si) => {
                                                                      const semKey = `${brKey}|${s.Semester}`;
                                                                      const semOpen = !!openSem[semKey];

                                                                      return (
                                                                        <React.Fragment key={semKey}>
                                                                          <TableRow
                                                                            sx={zebraRowSx(si, true)}
                                                                            onClick={() =>
                                                                              toggleSem(row, b.BranchCode, s.Semester)
                                                                            }
                                                                            role="button"
                                                                            tabIndex={0}
                                                                            onKeyDown={(e) => {
                                                                              if (
                                                                                e.key === "Enter" ||
                                                                                e.key === " "
                                                                              ) {
                                                                                e.preventDefault();
                                                                                toggleSem(
                                                                                  row,
                                                                                  b.BranchCode,
                                                                                  s.Semester
                                                                                );
                                                                              }
                                                                            }}
                                                                            aria-expanded={semOpen}
                                                                          >
                                                                            <TableCell>
                                                                              <Tooltip
                                                                                title={semOpen ? "Collapse" : "Expand"}
                                                                              >
                                                                                <IconButton
                                                                                  sx={iconBtnSx}
                                                                                  size="small"
                                                                                  onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    toggleSem(
                                                                                      row,
                                                                                      b.BranchCode,
                                                                                      s.Semester
                                                                                    );
                                                                                  }}
                                                                                >
                                                                                  {semOpen ? (
                                                                                    <ExpandLessIcon />
                                                                                  ) : (
                                                                                    <ExpandMoreIcon />
                                                                                  )}
                                                                                </IconButton>
                                                                              </Tooltip>
                                                                            </TableCell>

                                                                            <TableCell>{s.Semester}</TableCell>
                                                                            <TableCell>{s.Count}</TableCell>
                                                                          </TableRow>

                                                                          <TableRow>
                                                                            <TableCell
                                                                              colSpan={3}
                                                                              sx={{ p: 0, border: 0 }}
                                                                            >
                                                                              <Collapse
                                                                                in={semOpen}
                                                                                timeout="auto"
                                                                                unmountOnExit
                                                                              >
                                                                                <Box
                                                                                  sx={{
                                                                                    ml: 2,
                                                                                    my: 1,
                                                                                    ...surfaceSx,
                                                                                    overflow: "hidden",
                                                                                  }}
                                                                                >
                                                                                  <Box sx={{ p: 1.5 }}>
                                                                                    <Box sx={titleRowSx}>
                                                                                      <BadgeIcon sx={titleIconSx} />
                                                                                      <Typography
                                                                                        sx={{
                                                                                          fontWeight: 900,
                                                                                          color: THEME_ORANGE,
                                                                                        }}
                                                                                      >
                                                                                        Students
                                                                                      </Typography>
                                                                                    </Box>

                                                                                    {loadingKey === semKey ? (
                                                                                      <CircularProgress size={14} />
                                                                                    ) : (
                                                                                      <Paper
                                                                                        elevation={0}
                                                                                        sx={{ ...tableCardSx, mt: 0 }}
                                                                                      >
                                                                                        <Box sx={scrollClipSx}>
                                                                                          <Table
                                                                                            size="small"
                                                                                            sx={tableSx}
                                                                                          >
                                                                                            <TableHead>
                                                                                              <TableRow sx={theadRowSx}>
                                                                                                {headCell(
                                                                                                  <BadgeIcon fontSize="small" />,
                                                                                                  "Student ID"
                                                                                                )}
                                                                                                {headCell(
                                                                                                  <GroupsIcon fontSize="small" />,
                                                                                                  "Full Name"
                                                                                                )}
                                                                                                {headCell(
                                                                                                  <CurrencyRupeeIcon fontSize="small" />,
                                                                                                  "Fee Amount",
                                                                                                  { textAlign: "right" }
                                                                                                )}
                                                                                              </TableRow>
                                                                                            </TableHead>

                                                                                            <TableBody>
                                                                                              {(students[semKey] || []).map(
                                                                                                (st, sti) => (
                                                                                                  <TableRow
                                                                                                    key={`${st.StudentID}-${sti}`}
                                                                                                    sx={zebraRowSx(sti)}
                                                                                                  >
                                                                                                    <TableCell>
                                                                                                      {st.StudentID}
                                                                                                    </TableCell>
                                                                                                    <TableCell>
                                                                                                      {st.FullName}
                                                                                                    </TableCell>
                                                                                                    <TableCell align="right">
                                                                                                      {st.FeeAmount ?? "-"}
                                                                                                    </TableCell>
                                                                                                  </TableRow>
                                                                                                )
                                                                                              )}
                                                                                            </TableBody>
                                                                                          </Table>
                                                                                        </Box>
                                                                                      </Paper>
                                                                                    )}
                                                                                  </Box>
                                                                                </Box>
                                                                              </Collapse>
                                                                            </TableCell>
                                                                          </TableRow>
                                                                        </React.Fragment>
                                                                      );
                                                                    })}
                                                                  </TableBody>
                                                                </Table>
                                                              </Box>
                                                            </Paper>
                                                          )}
                                                        </Box>
                                                      </Box>
                                                    </Collapse>
                                                  </TableCell>
                                                </TableRow>
                                              </React.Fragment>
                                            );
                                          })}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  </Paper>
                                )}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    </>
  );
}

/* ----------------------- Main component ----------------------- */
const FeeManagement = () => {
  const [students, setStudents] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  const [selectedFee, setSelectedFee] = useState("");
  const [feeAmount, setFeeAmount] = useState("");
  const [masterStudents, setMasterStudents] = useState([]);

  const [activeTab, setActiveTab] = useState(0);
  const reconciliationFileInputRef = useRef(null);
  const [reconciliationFile, setReconciliationFile] = useState(null);
  const [reconciliationBusy, setReconciliationBusy] = useState(false);
  const [reconciliationInfo, setReconciliationInfo] = useState("");
  const [reconciliationResults, setReconciliationResults] = useState([]);
  const [reconciliationSummary, setReconciliationSummary] = useState(null);

  const token = localStorage.getItem("auth");
  const fileInputRef = useRef();

  const [uploadInfo, setUploadInfo] = useState("");
  const [filters, setFilters] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Semester: [],
    StudentID: [],
    IsStudentActive: [],
  });

  const [studentIdInput, setStudentIdInput] = useState("");
  const [logincampusCode, setCampusCode] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [feeSummary, setFeeSummary] = useState([]);

  const handleReconciliationFileClick = () => {
    reconciliationFileInputRef.current?.click();
  };

  const handleReconciliationFileSelected = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const isExcel =
      file.name.toLowerCase().endsWith(".xlsx") ||
      file.name.toLowerCase().endsWith(".xls");

    if (!isExcel) {
      setError("Please select an Excel file (.xlsx or .xls).");
      event.target.value = "";
      return;
    }

    setError("");
    setReconciliationInfo("");
    setReconciliationResults([]);
    setReconciliationSummary(null);
    setReconciliationFile(file);
  };

  const uploadReconciliationFile = async () => {
    if (!reconciliationFile) {
      setError("Please select a bank reconciliation Excel file.");
      return;
    }
  
    try {
      setError("");
      setReconciliationInfo("");
      setReconciliationBusy(true);
      setReconciliationResults([]);
      setReconciliationSummary(null);
  
      const formData = new FormData();
  
      formData.append(
        "reconciliationFile",
        reconciliationFile
      );
  
      const response = await axios.post(
        `${API_URL}/api/payments/admin/external-reconciliation/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
  
      const data = response.data || {};
  
      setReconciliationSummary({
        totalRows: Number(data.totalRows || 0),
        matched: Number(data.matched || 0),
        fallbackMatched: Number(data.fallbackMatched || 0),
        alreadyReconciled: Number(data.alreadyReconciled || 0),
        notFound: Number(data.notFound || 0),
        amountMismatch: Number(data.amountMismatch || 0),
        ambiguous: Number(data.ambiguous || 0),
        invalidRows: Number(data.invalidRows || 0),
        errors: Number(data.errors || 0),
      });
  
      setReconciliationResults(
        Array.isArray(data.results)
          ? data.results
          : []
      );
  
      setReconciliationInfo(
        `Reconciliation completed. ` +
          `Matched=${Number(data.matched || 0)}, ` +
          `Fallback Matched=${Number(data.fallbackMatched || 0)}, ` +
          `Not Found=${Number(data.notFound || 0)}, ` +
          `Amount Mismatch=${Number(data.amountMismatch || 0)}, ` +
          `Ambiguous=${Number(data.ambiguous || 0)}`
      );
  
      setSuccess(true);
      setReconciliationFile(null);
  
      if (reconciliationFileInputRef.current) {
        reconciliationFileInputRef.current.value = "";
      }
  
      await fetchFeeSummary();
    } catch (err) {
      console.error(
        "External reconciliation upload failed",
        err
      );
  
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "External reconciliation upload failed."
      );
    } finally {
      setReconciliationBusy(false);
    }
  };

  const downloadReconciliationSample = () => {
    const rows = [
      {
        ReferenceNumber: "BIC5YV60RQ46CT",
        StudentID: "22Q1A1124",
        TxnAmount: 6000,
        FeeType: "Semi Residential / transport",
        Semester: "41",
        TransactionID: "BANKTXN123456",
        TransactionDate: "2026-07-19",
      },
    ];
  
    const worksheet = XLSX.utils.json_to_sheet(rows);
  
    worksheet["!cols"] = [
      { wch: 24 }, // ReferenceNumber
      { wch: 16 }, // StudentID
      { wch: 16 }, // TxnAmount
      { wch: 36 }, // FeeType
      { wch: 12 }, // Semester
      { wch: 24 }, // TransactionID
      { wch: 20 }, // TransactionDate
    ];
  
    const instructions = [
      ["Column", "Required", "Purpose"],
      [
        "ReferenceNumber",
        "Required",
        "Bank payment reference. UniFLOO first tries to match this against the reference submitted by the student.",
      ],
      [
        "StudentID",
        "Required for fallback",
        "Used when ReferenceNumber is missing or does not match a UniFLOO cart.",
      ],
      [
        "TxnAmount",
        "Required",
        "Actual amount reported by the bank. It must exactly match the UniFLOO cart amount.",
      ],
      [
        "FeeType",
        "Required for fallback",
        "Fee type selected by the student, such as TUITION, HOSTEL, TRANSPORT, or the configured bundle label.",
      ],
      [
        "Semester",
        "Optional",
        "Semester to reconcile. When blank, UniFLOO uses the latest semester available for that student and fee type.",
      ],
      [
        "TransactionID",
        "Optional",
        "Bank transaction ID or UTR. Stored as the gateway payment ID.",
      ],
      [
        "TransactionDate",
        "Optional",
        "Bank transaction date. Currently retained in the uploaded file but not used for matching.",
      ],
      [
        "Fallback rule",
        "",
        "StudentID + TxnAmount + FeeType + Semester must identify exactly one external payment cart.",
      ],
      [
        "Amount mismatch",
        "",
        "When bank amount differs from the UniFLOO cart amount, no fee is updated.",
      ],
      [
        "Multiple matches",
        "",
        "When more than one cart matches, the row is marked Ambiguous and requires manual review.",
      ],
    ];
  
    const instructionSheet =
      XLSX.utils.aoa_to_sheet(instructions);
  
    instructionSheet["!cols"] = [
      { wch: 24 },
      { wch: 24 },
      { wch: 110 },
    ];
  
    const workbook = XLSX.utils.book_new();
  
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Reconciliation"
    );
  
    XLSX.utils.book_append_sheet(
      workbook,
      instructionSheet,
      "Instructions"
    );
  
    const output = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
  
    const blob = new Blob([output], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  
    saveAs(
      blob,
      `ExternalPaymentReconciliation_${
        logincampusCode || "CAMPUS"
      }.xlsx`
    );
  };

  const reconciliationStatusChip = (status) => {
    switch (status) {
      case "MATCHED":
        return (
          <Chip
            size="small"
            icon={<CheckCircleIcon />}
            label="Reconciled"
            color="success"
            sx={{ fontWeight: 800 }}
          />
        );

      case "AMBIGUOUS_MATCH":
        return (
          <Chip
            size="small"
            icon={<ErrorOutlineIcon />}
            label="Ambiguous Match"
            color="warning"
            sx={{ fontWeight: 800 }}
          />
        );
      
      case "STUDENT_NOT_FOUND":
        return (
          <Chip
            size="small"
            icon={<ErrorOutlineIcon />}
            label="Student Not Found"
            color="error"
            sx={{ fontWeight: 800 }}
          />
        );
      
      case "SEMESTER_NOT_FOUND":
        return (
          <Chip
            size="small"
            icon={<ErrorOutlineIcon />}
            label="Semester Not Found"
            color="error"
            sx={{ fontWeight: 800 }}
          />
        );
  
      case "ALREADY_RECONCILED":
        return (
          <Chip
            size="small"
            icon={<CheckCircleIcon />}
            label="Already Reconciled"
            color="info"
            sx={{ fontWeight: 800 }}
          />
        );
  
      case "AMOUNT_MISMATCH":
        return (
          <Chip
            size="small"
            icon={<ErrorOutlineIcon />}
            label="Amount Mismatch — Not Updated"
            color="warning"
            sx={{ fontWeight: 800 }}
          />
        );
  
      case "NOT_FOUND":
        return (
          <Chip
            size="small"
            icon={<ErrorOutlineIcon />}
            label="Reference Not Found"
            color="error"
            sx={{ fontWeight: 800 }}
          />
        );
  
      case "INVALID_ROW":
        return (
          <Chip
            size="small"
            icon={<ErrorOutlineIcon />}
            label="Invalid Row"
            variant="outlined"
            sx={{ fontWeight: 800 }}
          />
        );
  
      default:
        return (
          <Chip
            size="small"
            icon={<HourglassTopIcon />}
            label={status || "Unknown"}
            variant="outlined"
          />
        );
    }
  };

  const parseIDs = (text) => {
    const tokens = String(text)
      .split(/[\s,;]+/g)
      .map((t) => t.trim())
      .filter(Boolean);

    const seen = new Set();
    const out = [];

    for (const t of tokens) {
      if (!seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    }

    return out;
  };

  const uniq = (arr) => Array.from(new Set(arr.filter((x) => x != null)));

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        setCampusCode(decodedToken.campusCode);
      } catch {
        setError("Failed to decode authentication token.");
      }
    }
  }, [token]);

  useEffect(() => {
    if (!logincampusCode) return;

    axios
      .get(`${API_URL}/api/students-with-fees`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setMasterStudents(r.data))
      .catch(() => setError("Failed to fetch filter options"));
  }, [logincampusCode, token]);

  useEffect(() => {
    if (!logincampusCode) return;

    fetchStudents();
    fetchFeeTypes();
    fetchFeeSummary();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logincampusCode]);

  useEffect(() => {
    if (!logincampusCode) return;

    fetchStudents();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const paramsSerializer = (params) => {
    const usp = new URLSearchParams();

    Object.entries(params).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        if (v.length === 0) return;
        v.forEach((val) => usp.append(k, val));
      } else if (v !== undefined && v !== null && v !== "") {
        usp.append(k, v);
      }
    });

    return usp.toString();
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/students-with-fees`, {
        params: { CampusCode: logincampusCode, ...filters },
        paramsSerializer,
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents(response.data);
    } catch {
      setError("Failed to fetch students");
    }
  };

  const fetchFeeTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/fee-types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeeTypes(response.data);
    } catch {
      setError("Failed to fetch fee types");
    }
  };

  const fetchFeeSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/fee-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeeSummary(response.data);
    } catch {
      setError("Failed to fetch fee summary.");
    }
  };

  const handleBulkAddFees = async () => {
    if (!selectedFee) {
      setError("Please select a fee type and set the fee amount.");
      return;
    }

    const selectedFeeObject = feeTypes.find((fee) => fee.FeeStructureID === selectedFee);

    if (!selectedFeeObject) {
      setError("Invalid fee selection.");
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/bulk-add-fees`,
        {
          Filters: filters,
          FeeStructureID: selectedFeeObject.FeeStructureID,
          FeeType: selectedFeeObject.FeeType,
          Amount: feeAmount,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(true);
      setError("");
      setSelectedFee("");
      setFeeAmount("");
      fetchFeeSummary();
    } catch {
      setError("Failed to add fee to filtered students.");
    }
  };

  const options = useMemo(
    () => ({
      SubCampusCode: uniq(masterStudents.map((s) => s.SubCampusCode)).sort(),
      DegreeCode: uniq(masterStudents.map((s) => s.DegreeCode)).sort(),
      BranchCode: uniq(masterStudents.map((s) => s.BranchCode)).sort(),
      Semester: uniq(masterStudents.map((s) => s.Semester)).sort((a, b) =>
        String(a).localeCompare(String(b))
      ),
      IsStudentActive: ["1", "0"],
    }),
    [masterStudents]
  );

  const downloadStudentFeesHistorySample = () => {
    const cc = logincampusCode;

    const headers = [
      "StudentID",
      "Semester",
      "FeeType",
      "FeeDate",
      "OriginalFeeAmount",
      "DiscountAmount",
      "RefundAmount",
      "FeePaid",
    ];

    const sampleRows = [
      {
        StudentID: "23A91A0001",
        Semester: "1",
        FeeType: "TUITION",
        FeeDate: "2024-07-01",
        OriginalFeeAmount: 50000,
        DiscountAmount: 0,
        RefundAmount: 0,
        FeePaid: 20000,
      },
      {
        StudentID: "23A91A0001",
        Semester: "1",
        FeeType: "HOSTEL",
        FeeDate: "2024-07-01",
        OriginalFeeAmount: 30000,
        DiscountAmount: 0,
        RefundAmount: 0,
        FeePaid: 0,
      },
    ];

    const ws = XLSX.utils.json_to_sheet(sampleRows, { header: headers });
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(16, h.length + 2) }));

    const instructions = [
      ["Field / Rule", "Instruction"],
      ["Sheet", "You can keep any sheet name (first sheet is read)"],
      ["CampusCode", "NOT required in sheet. Taken from JWT campusCode automatically."],
      ["StudentID", "Required. Must exist in studentpersonalinformation for this campus."],
      ["Semester", "Required. Example: 1, 2, 3... (stored as varchar(2))."],
      ["FeeType", "Required. Must exist in studentfeestructure for this CampusCode."],
      ["FeeDate", "Required. Date of that historical entry. Use YYYY-MM-DD."],
      ["OriginalFeeAmount", "Required (>0). This becomes FeeAmount generated column."],
      ["FeePaid", "Optional. Amount already paid for that entry."],
      ["DiscountAmount / RefundAmount", "Optional. Defaults to 0."],
      [
        "Duplicates",
        "If same StudentID+Semester+FeeType+FeeDate is uploaded again, it will UPDATE existing row.",
      ],
    ];

    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    ws2["!cols"] = [{ wch: 22 }, { wch: 90 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "StudentFeesHistory");
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `StudentFeesHistory_Sample_${cc || "CAMPUS"}.xlsx`);
  };

  const handleBulkHistoryClick = () => fileInputRef.current?.click();

  const handleStudentFeesHistoryFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");
    setUploadInfo("");

    const form = new FormData();
    form.append("excelFile", file);

    try {
      const res = await axios.post(`${API_URL}/api/UploadStudentFeesHistory`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        validateStatus: (s) => s >= 200 && s < 300,
      });

      const data = res.data || {};
      const total = data.totalRows ?? data.total ?? 0;
      const processed = data.processed ?? 0;
      const errorsCount = data.errorsCount ?? data.errorCount ?? 0;

      setUploadInfo(
        `Upload done. Total=${total}, Processed=${processed}, Errors=${errorsCount}` +
          (errorsCount ? ` (showing up to 50)` : "")
      );

      setSuccess(true);
      await fetchFeeSummary();

      if (Array.isArray(data.errors) && data.errors.length) {
        console.warn("Upload row errors first 50:", data.errors);
      }
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || err?.response?.data?.message || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 34 }} />
            <Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
                Student Fee Add
              </Typography>
              <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
                Filter students and bulk add a fee
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              sx={outlinedBtnSx}
              startIcon={<DownloadIcon />}
              onClick={downloadStudentFeesHistorySample}
            >
              Download History Sample
            </Button>

            <Button
              variant="contained"
              sx={primaryBtnSx}
              startIcon={<UploadIcon />}
              onClick={handleBulkHistoryClick}
            >
              Bulk Upload History
            </Button>

            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleStudentFeesHistoryFile}
            />
          </Box>
        </Box>

        <Box sx={bodySx}>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Fee successfully added to filtered students!
              </Alert>
            </Snackbar>
          )}

          {uploadInfo && (
            <Alert
              severity="success"
              onClose={() => setUploadInfo("")}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid rgba(25,118,210,0.18)",
                backgroundColor: "rgba(25,118,210,0.04)",
              }}
            >
              {uploadInfo}
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{
              ...surfaceSx,
              mb: 2,
              overflow: "hidden",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(event, newValue) =>
                setActiveTab(newValue)
              }
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom:
                  "1px solid rgba(25,118,210,0.12)",
              
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 900,
                  minHeight: 54,
                },
              
                "& .Mui-selected": {
                  color: `${THEME_ORANGE} !important`,
                },
              
                "& .MuiTabs-indicator": {
                  backgroundColor: THEME_ORANGE,
                  height: 3,
                },
              }}
            >
              <Tab
                icon={<AddCardIcon />}
                iconPosition="start"
                label="Fee Upload"
              />

              <Tab
                icon={<FactCheckIcon />}
                iconPosition="start"
                label="Bulk Reconciliation Upload"
              />
            </Tabs>
          </Paper>

        {activeTab === 0 && (
        <>
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Box sx={titleRowSx}>
                <FilterAltIcon sx={titleIconSx} />
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                  Filters
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={options.SubCampusCode}
                    value={filters.SubCampusCode}
                    onChange={(e, v) => setFilters((p) => ({ ...p, SubCampusCode: v }))}
                    renderInput={(params) => (
                      <TextField {...params} label="Sub Campus" size="small" sx={inputSx} />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={options.DegreeCode}
                    value={filters.DegreeCode}
                    onChange={(e, v) => setFilters((p) => ({ ...p, DegreeCode: v }))}
                    renderInput={(params) => (
                      <TextField {...params} label="Degree" size="small" sx={inputSx} />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={options.BranchCode}
                    value={filters.BranchCode}
                    onChange={(e, v) => setFilters((p) => ({ ...p, BranchCode: v }))}
                    renderInput={(params) => (
                      <TextField {...params} label="Branch" size="small" sx={inputSx} />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={options.Semester}
                    value={filters.Semester}
                    onChange={(e, v) => setFilters((p) => ({ ...p, Semester: v }))}
                    renderInput={(params) => (
                      <TextField {...params} label="Semester" size="small" sx={inputSx} />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Student IDs"
                    placeholder="Paste comma / space / newline separated IDs"
                    value={studentIdInput}
                    sx={inputSx}
                    onChange={(e) => {
                      const txt = e.target.value;
                      setStudentIdInput(txt);
                      const ids = parseIDs(txt);
                      setFilters((p) => ({ ...p, StudentID: ids }));
                    }}
                  />

                  {filters.StudentID.length > 0 && (
                    <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                      {filters.StudentID.map((id) => (
                        <Chip
                          key={id}
                          size="small"
                          icon={<BadgeIcon />}
                          label={id}
                          onDelete={() => {
                            const next = filters.StudentID.filter((x) => x !== id);
                            setFilters((p) => ({ ...p, StudentID: next }));
                            setStudentIdInput(next.join(", "));
                          }}
                          sx={{ ...chipSx, mb: 1 }}
                        />
                      ))}
                    </Stack>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
            <Box sx={{ pl: 1 }}>
              <Box sx={titleRowSx}>
                <AddCardIcon sx={titleIconSx} />
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                  Fee Type and Amount
                </Typography>
              </Box>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <Autocomplete
                    options={feeTypes}
                    getOptionLabel={(o) => o.FeeType || ""}
                    value={feeTypes.find((f) => f.FeeStructureID === selectedFee) || null}
                    onChange={(e, val) => setSelectedFee(val ? val.FeeStructureID : "")}
                    renderInput={(params) => (
                      <TextField {...params} label="Fee Type" size="small" sx={inputSx} />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    label="Fee Amount"
                    type="number"
                    size="small"
                    value={feeAmount}
                    sx={inputSx}
                    onChange={(e) => {
                      const value = e.target.value.trim() === "" ? "" : parseFloat(e.target.value);

                      if (value !== "" && value <= 0) {
                        setError("Fee amount must be greater than 0");
                        return;
                      }

                      setError("");
                      setFeeAmount(value);
                    }}
                    InputProps={{
                      startAdornment: (
                        <CurrencyRupeeIcon
                          sx={{ color: "rgba(25,118,210,0.75)", mr: 0.5 }}
                          fontSize="small"
                        />
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <Button
                    variant="contained"
                    sx={primaryBtnSx}
                    fullWidth
                    startIcon={<AddCardIcon />}
                    onClick={handleBulkAddFees}
                  >
                    Add Fee
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
            <Box sx={{ pl: 1 }}>
              <Box sx={titleRowSx}>
                <SummarizeIcon sx={titleIconSx} />
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                  Fee Summary
                </Typography>
              </Box>

              <FeeSummaryDrilldown campusCode={logincampusCode} feeSummary={feeSummary} />
            </Box>
          </Paper>
        </>
      )}

      {activeTab === 1 && (
        <>
          <Paper
            variant="outlined"
            sx={sectionSx}
          >
            <Box sx={{ pl: 1 }}>
              <Box sx={titleRowSx}>
                <FactCheckIcon sx={titleIconSx} />
      
                <Typography
                  sx={{
                    fontWeight: 900,
                    color: THEME_ORANGE,
                  }}
                >
                  External Payment Reconciliation
                </Typography>
              </Box>
                
              <Alert
                severity="info"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                }}
              >
                UniFLOO first matches <strong>ReferenceNumber</strong> and verifies
                <strong> TxnAmount</strong>. When the reference is missing or incorrect,
                it uses <strong>StudentID + TxnAmount + FeeType + Semester</strong>.
                When Semester is blank, the student's latest semester for that fee type
                is used. Payments are updated only when one unique cart is found and the
                bank amount exactly matches the UniFLOO cart amount.
              </Alert>
              
              <Grid
                container
                spacing={2}
                alignItems="center"
              >
                <Grid
                  item
                  xs={12}
                  md={5}
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="Selected Bank File"
                    value={
                      reconciliationFile?.name || ""
                    }
                    placeholder="No file selected"
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={inputSx}
                  />
                </Grid>
                  
                <Grid
                  item
                  xs={12}
                  sm={4}
                  md={2}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={
                      handleReconciliationFileClick
                    }
                    sx={outlinedBtnSx}
                    disabled={reconciliationBusy}
                  >
                    Select File
                  </Button>
                  
                  <input
                    ref={
                      reconciliationFileInputRef
                    }
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: "none" }}
                    onChange={
                      handleReconciliationFileSelected
                    }
                  />
                </Grid>
                  
                <Grid
                  item
                  xs={12}
                  sm={4}
                  md={2}
                >
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={
                      reconciliationBusy ? (
                        <CircularProgress
                          size={18}
                          color="inherit"
                        />
                      ) : (
                        <FactCheckIcon />
                      )
                    }
                    disabled={
                      reconciliationBusy ||
                      !reconciliationFile
                    }
                    onClick={
                      uploadReconciliationFile
                    }
                    sx={primaryBtnSx}
                  >
                    Reconcile
                  </Button>
                </Grid>
                  
                <Grid
                  item
                  xs={12}
                  sm={4}
                  md={3}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={
                      downloadReconciliationSample
                    }
                    sx={outlinedBtnSx}
                  >
                    Download Sample
                  </Button>
                </Grid>
              </Grid>
                  
              {reconciliationBusy && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                </Box>
              )}

              {reconciliationFile && (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                  }}
                >
                  Selected file:{" "}
                  <strong>
                    {reconciliationFile.name}
                  </strong>
                </Alert>
              )}
            </Box>
          </Paper>
            
          {reconciliationInfo && (
            <Alert
              severity="success"
              onClose={() =>
                setReconciliationInfo("")
              }
              sx={{
                mt: 2,
                borderRadius: 2,
              }}
            >
              {reconciliationInfo}
            </Alert>
          )}

          {reconciliationSummary && (
            <Paper
              variant="outlined"
              sx={{
                ...sectionSx,
                mt: 2,
              }}
            >
              <Box sx={{ pl: 1 }}>
                <Box sx={titleRowSx}>
                  <SummarizeIcon sx={titleIconSx} />
            
                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: THEME_ORANGE,
                    }}
                  >
                    Reconciliation Summary
                  </Typography>
                </Box>
                  
                <Grid container spacing={2}>
                  {[
                    {
                      label: "Total Rows",
                      value: reconciliationSummary.totalRows,
                    },
                    {
                      label: "Reconciled",
                      value: reconciliationSummary.matched,
                    },
                    {
                      label: "Fallback Matched",
                      value: reconciliationSummary.fallbackMatched,
                    },
                    {
                      label: "Already Reconciled",
                      value: reconciliationSummary.alreadyReconciled,
                    },
                    {
                      label: "Reference / Cart Not Found",
                      value: reconciliationSummary.notFound,
                    },
                    {
                      label: "Amount Mismatch",
                      value: reconciliationSummary.amountMismatch,
                    },
                    {
                      label: "Ambiguous",
                      value: reconciliationSummary.ambiguous,
                    },
                    {
                      label: "Invalid Rows",
                      value: reconciliationSummary.invalidRows,
                    },
                    {
                      label: "Errors",
                      value: reconciliationSummary.errors,
                    },
                  ].map((item) => (
                    <Grid
                      item
                      xs={6}
                      sm={4}
                      md={2}
                      key={item.label}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          textAlign: "center",
                          borderColor: BORDER,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "1.3rem",
                            fontWeight: 900,
                            color: "#1565C0",
                          }}
                        >
                          {item.value}
                        </Typography>
                        
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {item.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Paper>
          )}

          {reconciliationResults.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                ...tableCardSx,
                mt: 2,
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  borderBottom:
                    "1px solid rgba(25,118,210,0.12)",
                }}
              >
                <HistoryIcon
                  sx={{ color: THEME_ORANGE }}
                />

                <Typography
                  sx={{
                    fontWeight: 900,
                    color: "#1565C0",
                  }}
                >
                  Reconciliation Results
                </Typography>
              </Box>
                
              <Box sx={scrollClipSx}>
                <TableContainer component="div">
                  <Table
                    size="small"
                    sx={tableSx}
                  >
                    <TableHead>
                      <TableRow sx={theadRowSx}>
                        <TableCell>Row</TableCell>
                        <TableCell>Reference Number</TableCell>
                        <TableCell>Student ID</TableCell>
                        <TableCell>Fee Type</TableCell>
                        <TableCell>Uploaded Semester</TableCell>
                        <TableCell>Applied Semester</TableCell>
                        <TableCell align="right">Bank Amount</TableCell>
                        <TableCell align="right">UniFLOO Amount</TableCell>
                        <TableCell>Cart Reference</TableCell>
                        <TableCell>Match Method</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Message</TableCell>
                      </TableRow>
                    </TableHead>
                
                    <TableBody>
                      {reconciliationResults.map(
                        (row, index) => (
                          <TableRow
                            key={`${row.rowNumber}-${row.paymentReference || "NOREF"}-${index}`}
                            sx={zebraRowSx(index)}
                          >
                            <TableCell>{row.rowNumber}</TableCell>
                                                  
                            <TableCell sx={{ fontWeight: 800 }}>
                              {row.paymentReference || "-"}
                            </TableCell>
                                                  
                            <TableCell>
                              {row.studentId || "-"}
                            </TableCell>
                                                  
                            <TableCell>
                              {row.feeType || "-"}
                            </TableCell>
                                                  
                            <TableCell>
                              {row.semester || "-"}
                            </TableCell>
                                                  
                            <TableCell>
                              {row.resolvedSemester || "-"}
                            </TableCell>
                                                  
                            <TableCell align="right">
                              ₹{Number(row.bankAmount || 0).toFixed(2)}
                            </TableCell>
                                                  
                            <TableCell align="right">
                              {row.cartAmount == null
                                ? "-"
                                : `₹${Number(row.cartAmount).toFixed(2)}`}
                            </TableCell>
                              
                            <TableCell>
                              {row.clientTxnRef || "-"}
                            </TableCell>
                              
                            <TableCell>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={
                                  row.matchMethod === "REFERENCE"
                                    ? "Reference"
                                    : row.matchMethod === "FALLBACK_NO_REFERENCE"
                                    ? "Fallback — No Reference"
                                    : row.matchMethod === "FALLBACK_AFTER_REFERENCE_NOT_FOUND"
                                    ? "Fallback — Incorrect Reference"
                                    : row.matchMethod || "-"
                                }
                                sx={{ fontWeight: 700 }}
                              />
                            </TableCell>
                              
                            <TableCell>
                              {reconciliationStatusChip(row.status)}
                            </TableCell>
                              
                            <TableCell>
                              {row.message || "-"}
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          )}
        </>
      )}
        </Box>
      </Paper>
    </Box>
  );
};

export default FeeManagement;